import type { Editor } from '@tiptap/react';

import { useEditorState } from '@tiptap/react';
import { TableCellsMergeIcon, TableCellsSplitIcon } from 'lucide-react';
import { useCallback } from 'react';

import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useTiptapEditor } from '@/hooks/tiptap';
import { hasExtension, isNodeInSchema, shouldShow } from '@/lib/tiptap/tiptapUtils';

import type { CellActionButtonProps, CellActionType } from '.';

export const cellActionIcons = {
  splitCell: TableCellsSplitIcon,
  mergeCells: TableCellsMergeIcon,
};

function runCellAction(editor: Editor | null, type: CellActionType): void {
  if (!editor) return;

  if (type === 'splitCell') {
    editor.chain().focus().splitCell().run();
  } else {
    editor.chain().focus().mergeCells().run();
  }
}

function useIsCellActionActive(editor: Editor | null, type: CellActionType): boolean {
  return (
    useEditorState({
      editor,
      selector: (ctx) => ctx.editor?.isActive(type),
    }) ?? false
  );
}

function useIsCellActionDisabled(editor: Editor | null, type: CellActionType, userDisabled: boolean = false): boolean {
  const canDo: boolean =
    useEditorState({
      editor,
      selector: (ctx) => {
        if (!hasExtension(ctx.editor, 'table')) return false;
        if (type === 'splitCell') {
          return ctx.editor?.can().splitCell();
        }
        return ctx.editor?.can().mergeCells();
      },
    }) ?? false;
  if (userDisabled) return true;
  if (editor?.isActive(type)) return true;
  if (!canDo) return true;
  return false;
}

const useCellActionState = (editor: Editor | null, type: CellActionType, disabled?: boolean) => {
  const isDisabled: boolean = useIsCellActionDisabled(editor, type, disabled);
  const isActive: boolean = useIsCellActionActive(editor, type);

  return {
    isActive,
    isDisabled,
  };
};

function useHandleClick(onClick: CellActionButtonProps['onClick'], callback: () => void) {
  return useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      onClick?.(e);
      if (!e.defaultPrevented) {
        callback();
      }
    },
    [onClick, callback]
  );
}

export function CellActionButton(cellActionButtonProps: CellActionButtonProps) {
  const { editor: providedEditor, onClick, children, disabled, type, ...props } = cellActionButtonProps;
  const editor: Editor | null = useTiptapEditor(providedEditor);
  const { isActive, isDisabled } = useCellActionState(editor, type, disabled);
  const Icon = cellActionIcons[type];
  const handleClick = useHandleClick(onClick, () => {
    runCellAction(editor, type);
  });

  const inSchema: boolean = isNodeInSchema('table', editor);
  const show: boolean = shouldShow([!!editor, inSchema]);
  if (!show || !editor || !editor.isEditable) {
    return null;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          data-slot={`${type}-button`}
          disabled={isDisabled}
          data-active-state={isActive ? 'on' : 'off'}
          data-disabled={isDisabled}
          aria-pressed={isActive}
          onClick={handleClick}
          {...props}
        >
          {children || <Icon />}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{type === 'splitCell' ? 'Podziel komórkę' : 'Scal komórki'}</TooltipContent>
    </Tooltip>
  );
}
